import type {
    RealtimeConnectionState,
    RealtimeJobStatus,
} from '@quipex/core/realtime';

export const ACTIVITY_STAGES = [
    'intake',
    'classification',
    'enrichment',
    'preview',
    'export',
    'generic',
] as const;

export type ActivityStage = (typeof ACTIVITY_STAGES)[number];
export type ActivityKey = `batch:${string}` | `job:${string}`;
export type ActivityBatchKey = Extract<ActivityKey, `batch:${string}`>;
export type ActivityAvailability = 'available' | 'stale' | 'unavailable';
export type ActivitySection =
    | 'in-progress'
    | 'needs-attention'
    | 'ready'
    | 'earlier';

export interface ActivityIdentity {
    readonly userId: string;
    readonly organisationId: string;
}

export function activityIdentitiesEqual(
    left: ActivityIdentity | null,
    right: ActivityIdentity | null
): boolean {
    return (
        left === right ||
        (left !== null &&
            right !== null &&
            left.userId === right.userId &&
            left.organisationId === right.organisationId)
    );
}

export function activityIdentityForLoadedContext(
    activeIdentity: ActivityIdentity | null,
    userId: string | null,
    organisationId: string | null
): ActivityIdentity | null {
    if (!userId) return null;
    if (organisationId) return { userId, organisationId };

    return activeIdentity?.userId === userId ? activeIdentity : null;
}

export function isActivityBatchKey(key: ActivityKey): key is ActivityBatchKey {
    return key.startsWith('batch:');
}

export interface ActivityJob {
    readonly jobId: string;
    readonly jobType: string;
    readonly stage: ActivityStage;
    readonly status: RealtimeJobStatus;
    readonly warning: boolean;
    readonly percentComplete: number | null;
    readonly completedItems?: number | null;
    readonly totalItems?: number | null;
    readonly message: string | null;
    readonly occurredAtUtc: string;
    readonly documentPublicId: string | null;
    readonly buildingPublicId: string | null;
    readonly packId: string | null;
    readonly exportKind?: 'documents' | 'selected-documents' | 'pack' | null;
    readonly selectedSelectionKey?: string;
    readonly downloadAvailable?: boolean;
    readonly expiresAt?: string | null;
    readonly buildingName?: string | null;
}

export interface ActivityJobUpdate extends ActivityJob {
    readonly key: ActivityKey;
    readonly correlationId: string | null;
}

export interface ActivityObservedIntake {
    readonly jobType: 'DocumentImport' | 'SharePointImport';
    readonly message: string | null;
    readonly occurredAtUtc: string;
}

export interface ActivityItem {
    readonly key: ActivityKey;
    readonly correlationId: string | null;
    readonly jobs: readonly ActivityJob[];
    readonly observedJobIds?: readonly string[];
    readonly createdAtUtc: string;
    readonly updatedAtUtc: string;
    readonly seen: boolean;
    readonly actedOn: boolean;
    readonly availability: ActivityAvailability;
    readonly identityQuarantined?: boolean;
    readonly observedIntake?: ActivityObservedIntake;
}

export interface ActivityDismissalTombstoneMetadata {
    readonly key: ActivityBatchKey;
    readonly observedJobIds: readonly string[];
    readonly latestObservedAtUtc: string;
}

export interface ActivitySession {
    readonly items: readonly ActivityItem[];
    readonly dismissalTombstones: readonly ActivityKey[];
    readonly dismissalTombstoneMetadata: readonly ActivityDismissalTombstoneMetadata[];
}

export type ActivityIntent =
    | {
          readonly type: 'view-receipt';
          readonly buildingPublicId: string;
          readonly batchId: string;
      }
    | {
          readonly type: 'open-preview';
          readonly buildingPublicId: string;
          readonly documentPublicId: string;
      }
    | {
          readonly type: 'download-export';
          readonly buildingPublicId: string;
          readonly jobId: string;
      }
    | {
          readonly type: 'download-selected-export';
          readonly buildingPublicId: string;
          readonly jobId: string;
      }
    | {
          readonly type: 'download-pack-export';
          readonly buildingPublicId: string;
          readonly packId: string;
          readonly jobId: string;
      };

export interface ActivitySections {
    readonly inProgress: readonly ActivityItem[];
    readonly needsAttention: readonly ActivityItem[];
    readonly ready: readonly ActivityItem[];
    readonly earlier: readonly ActivityItem[];
}

export interface ActivityConnectionStatus {
    readonly state: RealtimeConnectionState;
    readonly updatesDelayed: boolean;
}

export function createEmptyActivitySession(): ActivitySession {
    return {
        items: [],
        dismissalTombstones: [],
        dismissalTombstoneMetadata: [],
    };
}
